import React from 'react';
import {
  Text,
  Layout,
  Card,
  Information,
  Stack,
  withTranslation,
} from '@piwikpro/ui-components';
import { App } from '@piwikpro/apps-crate';
import { errorBoundary, withInject } from '@piwikpro/platform';
import ScrollTrackingComponent from '@piwikpro/container-settings-crate/components/ScrollTracking';
import SpaTrackingComponent from '@piwikpro/container-settings-crate/components/SpaTracking';
import GeneralDelay from './GeneralDelay';
import GeneralTimezoneAndCurrency from './GeneralTimezoneAndCurrency';

interface Props {
  app: App
  t: (key: string) => string
  Title: typeof React.Component
  SearchCategoryAndQueryParams: typeof React.Component
  StripSiteSearchQueryParams: typeof React.Component
  ExcludedUrlQueryParams: typeof React.Component
  KeepUrlFragment: typeof React.Component
  CampaignNameParams: typeof React.Component
  CampaignKeywordParams: typeof React.Component
  CampaignSourceParams: typeof React.Component
  CampaignMediumParams: typeof React.Component
  CampaignContentParams: typeof React.Component
  CampaignIdParams: typeof React.Component
  CreateNewVisitWhenCampaignChanges: typeof React.Component
  ExcludedIps: typeof React.Component
  ExcludeCrawlers: typeof React.Component
  ExcludedUserAgents: typeof React.Component
  ExcludeUnknownUrls: typeof React.Component
  CreateNewVisitWhenReferrerChanges: typeof React.Component
  Urls: typeof React.Component
  ScrollTracking: typeof ScrollTrackingComponent
  SpaTracking: typeof SpaTrackingComponent
  MaxSessionTime: typeof React.Component
  MaxSessionEventCount: typeof React.Component
  ActionOnSessionLimitExceeded: typeof React.Component
  AppDomainsTracking: typeof React.Component
}

const DataCollection: React.FC<Props> = ({
  app,
  t,
  Title,
  SearchCategoryAndQueryParams,
  StripSiteSearchQueryParams,
  ExcludedUrlQueryParams,
  KeepUrlFragment,
  CampaignNameParams,
  CampaignKeywordParams,
  CampaignSourceParams,
  CampaignMediumParams,
  CampaignContentParams,
  CampaignIdParams,
  CreateNewVisitWhenCampaignChanges,
  ExcludedIps,
  ExcludeCrawlers,
  ExcludedUserAgents,
  ExcludeUnknownUrls,
  CreateNewVisitWhenReferrerChanges,
  Urls,
  ScrollTracking,
  SpaTracking,
  MaxSessionTime,
  MaxSessionEventCount,
  ActionOnSessionLimitExceeded,
  AppDomainsTracking,
}) => (
  <Title part={t('apps.details.tabs.data-collection')}>
    <Layout>
      <Layout.AnnotatedSection
        title={t('apps.details.settings.basic-info.title')}
      />
      <Layout.Column dominant>
        <Card>
          <Card.Section>
            <Urls appId={app.id} />
          </Card.Section>
          <Card.Section>
            <GeneralTimezoneAndCurrency />
          </Card.Section>
        </Card>
      </Layout.Column>
    </Layout>
    <AppDomainsTracking appId={app.id} />
    <ScrollTracking appId={app.id} />
    <Layout>
      <Layout.AnnotatedSection
        title={t('apps.details.data-collection.internal-search-engine.category')}
      />
      <Layout.Column dominant>
        <Card>
          <Card.Section>
            <Stack vertical spacing="wide">
              <Stack.Item fill>
                <Stack vertical spacing="narrow">
                  <Stack.Item>
                    <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.data-collection.internal-search-engine.title')}</Text>
                  </Stack.Item>
                  <Stack.Item>
                    <Text as="p">{t('apps.details.data-collection.internal-search-engine.description')}</Text>
                  </Stack.Item>
                </Stack>
              </Stack.Item>
              <Stack.Item fill>
                <Information type="info">{t('apps.details.data-collection.internal-search-engine.help')}</Information>
              </Stack.Item>
            </Stack>
          </Card.Section>
          <Card.Section>
            <SearchCategoryAndQueryParams appId={app.id} />
          </Card.Section>
        </Card>
        <Card>
          <Card.Section>
            <StripSiteSearchQueryParams appId={app.id} />
          </Card.Section>
        </Card>
      </Layout.Column>
    </Layout>
    <Layout>
      <Layout.AnnotatedSection
        title={t('apps.details.settings.blacklisting.exclude-url-params.category')}
      />
      <Layout.Column dominant>
        <Card>
          <Card.Section>
            <Stack vertical>
              <Stack.Item fill>
                <Stack vertical spacing="narrow">
                  <Stack.Item>
                    <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.settings.blacklisting.exclude-url-params.title')}</Text>
                  </Stack.Item>
                  <Stack.Item>
                    <Text as="p">{t('apps.details.settings.blacklisting.exclude-url-params.description')}</Text>
                  </Stack.Item>
                </Stack>
              </Stack.Item>
              <Stack.Item fill>
                <ExcludedUrlQueryParams appId={app.id} />
              </Stack.Item>
            </Stack>
          </Card.Section>
        </Card>
        <Card>
          <Card.Section>
            <KeepUrlFragment appId={app.id} />
          </Card.Section>
        </Card>
      </Layout.Column>
    </Layout>
    <Layout>
      <Layout.AnnotatedSection
        title={t('apps.details.settings.campaign.title')}
      />
      <Layout.Column dominant>
        <Card>
          <Card.Section>
            <Stack vertical spacing="narrow">
              <Stack.Item>
                <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.settings.campaign-parameters.title')}</Text>
              </Stack.Item>
              <Stack.Item>
                <Text as="p">{t('apps.details.settings.campaign-parameters.description')}</Text>
              </Stack.Item>
            </Stack>
          </Card.Section>
          <CampaignNameParams appId={app.id} />
          <CampaignKeywordParams appId={app.id} />
          <CampaignSourceParams appId={app.id} />
          <CampaignMediumParams appId={app.id} />
          <CampaignContentParams appId={app.id} />
          <CampaignIdParams appId={app.id} />
        </Card>
        <Card>
          <Card.Section>
            <CreateNewVisitWhenCampaignChanges appId={app.id} />
          </Card.Section>
        </Card>
      </Layout.Column>
    </Layout>
    <Layout>
      <Layout.AnnotatedSection
        title={t('apps.details.settings.filters.title')}
      />
      <Layout.Column dominant>
        <Card>
          <Card.Section>
            <ExcludedIps appId={app.id} />
          </Card.Section>
        </Card>
        <Card>
          <Card.Section>
            <ExcludeCrawlers appId={app.id} />
          </Card.Section>
        </Card>
        <Card>
          <Card.Section>
            <Stack vertical spacing="narrow">
              <Stack.Item>
                <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.settings.filters.add-crawlers.title')}</Text>
              </Stack.Item>
              <Stack.Item>
                <Text as="p">{t('apps.details.settings.filters.add-crawlers.description')}</Text>
              </Stack.Item>
            </Stack>
            <ExcludedUserAgents appId={app.id} />
          </Card.Section>
        </Card>
        <Card>
          <Card.Section>
            <ExcludeUnknownUrls appId={app.id} />
          </Card.Section>
        </Card>
      </Layout.Column>
    </Layout>
    <SpaTracking appId={app.id} />
    <Layout>
      <Layout.AnnotatedSection
        title={t('apps.details.settings.session-limits.title')}
      />
      <Layout.Column dominant>
        <Card>
          <Card.Section>
            <Stack vertical spacing="narrow">
              <Stack.Item>
                <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.settings.session-limits.title')}</Text>
              </Stack.Item>
              <Stack.Item>
                <Text as="p">{t('apps.details.settings.session-limits.description')}</Text>
              </Stack.Item>
            </Stack>
          </Card.Section>
          <Card.Section>
            <MaxSessionTime appId={app.id} />
          </Card.Section>
          <Card.Section>
            <MaxSessionEventCount appId={app.id} />
          </Card.Section>
          <Card.Section>
            <ActionOnSessionLimitExceeded appId={app.id} />
          </Card.Section>
        </Card>
      </Layout.Column>
    </Layout>
    <Layout>
      <Layout.AnnotatedSection
        title={t('apps.details.settings.other-options.title')}
      />
      <Layout.Column dominant>
        <Card>
          <Card.Section>
            <GeneralDelay />
          </Card.Section>
        </Card>
        <Card>
          <Card.Section>
            <CreateNewVisitWhenReferrerChanges appId={app.id} />
          </Card.Section>
        </Card>
      </Layout.Column>
    </Layout>
  </Title>
);

export default withInject({
  Title: 'TitleCrate.components.Title',
  SearchCategoryAndQueryParams: 'TrackerSettingsCrate.components.AppSettings.SearchCategoryAndQueryParams',
  StripSiteSearchQueryParams: 'TrackerSettingsCrate.components.AppSettings.StripSiteSearchQueryParams',
  ExcludedUrlQueryParams: 'TrackerSettingsCrate.components.AppSettings.ExcludedUrlQueryParams',
  KeepUrlFragment: 'TrackerSettingsCrate.components.AppSettings.KeepUrlFragment',
  CampaignNameParams: 'TrackerSettingsCrate.components.AppSettings.CampaignNameParams',
  CampaignKeywordParams: 'TrackerSettingsCrate.components.AppSettings.CampaignKeywordParams',
  CampaignSourceParams: 'TrackerSettingsCrate.components.AppSettings.CampaignSourceParams',
  CampaignMediumParams: 'TrackerSettingsCrate.components.AppSettings.CampaignMediumParams',
  CampaignContentParams: 'TrackerSettingsCrate.components.AppSettings.CampaignContentParams',
  CampaignIdParams: 'TrackerSettingsCrate.components.AppSettings.CampaignIdParams',
  CreateNewVisitWhenCampaignChanges: 'TrackerSettingsCrate.components.AppSettings.CreateNewVisitWhenCampaignChanges',
  ExcludedIps: 'TrackerSettingsCrate.components.AppSettings.ExcludedIps',
  ExcludeCrawlers: 'TrackerSettingsCrate.components.AppSettings.ExcludeCrawlers',
  ExcludedUserAgents: 'TrackerSettingsCrate.components.AppSettings.ExcludedUserAgents',
  ExcludeUnknownUrls: 'TrackerSettingsCrate.components.AppSettings.ExcludeUnknownUrls',
  CreateNewVisitWhenReferrerChanges: 'TrackerSettingsCrate.components.AppSettings.CreateNewVisitWhenReferrerChanges',
  Urls: 'TrackerSettingsCrate.components.AppSettings.Urls',
  ScrollTracking: 'ContainerSettingsCrate.components.ScrollTracking',
  SpaTracking: 'ContainerSettingsCrate.components.SpaTracking',
  MaxSessionTime: 'TrackerSettingsCrate.components.AppSettings.MaxSessionTime',
  MaxSessionEventCount: 'TrackerSettingsCrate.components.AppSettings.MaxSessionEventCount',
  ActionOnSessionLimitExceeded: 'TrackerSettingsCrate.components.AppSettings.ActionOnSessionLimitExceeded',
  AppDomainsTracking: 'DomainsCrate.components.AppDomainsTracking',
})(
  withTranslation(['administration'])(errorBoundary(DataCollection)),
);
